import { useEffect, useState } from 'react';

import { toastService } from '@thefarmersfront/kitchen';

import { useQueryClient } from '@tanstack/react-query';
import type { IRowNode } from 'ag-grid-community';

import { ReceiveStatusDetailModal3PL, ReceiveStatusSearchFilter, ReceiveStatusTable } from '~/components/receive';

import { useDownloadReady, useModal } from '~/hooks/common';
import {
  receiveKeys,
  useGetFilterAllQuery,
  useGetInboundRequestProgressByRequestIdMutation,
  useGetInboundRequestProgressDownloadMutation,
  useGetInboundRequestProgressQuery,
  useGetReceivingSignSpecificationMutation,
} from '~/hooks/receive';
import { useUserInfoStore } from '~/store';
import { addIf, formatDate, getDateByOffset, isValidDateRange, renderFilterErrorMessage } from '~/utils';

import {
  INITIAL_RECEIVE_PAGE_CURSOR,
  INITIAL_RECEIVE_PAGE_SIZE,
  MAX_RECEIVE_DATE_RANGE,
  OPTION_ALL,
  TOAST_MESSAGE,
} from '~/constants';

import type {
  Type3PLReceiveStatusDetail,
  Type3PLReceiveStatusInfo,
  TypeGetInboundRequestProgressParams,
  TypeReceiveStatus3PLFilter,
  TypeReceiveStatusFilterStatus,
} from '~/types/receive.types';

const DEFAULT_RECEIVE_STATUS_3PL_FILTER: TypeReceiveStatus3PLFilter = {
  keywordType: { value: '', text: '' },
  keyword: '',
  center: OPTION_ALL,
  temperature: OPTION_ALL,
  dateType: { value: '', text: '' },
  dateRange: { start: getDateByOffset(-1, 'day'), end: getDateByOffset(0, 'day') },
  status: OPTION_ALL,
  releaseCode: OPTION_ALL,
  disallowAllYesNo: OPTION_ALL,
  hasReport: OPTION_ALL,
  reportStatus: OPTION_ALL,
  pagination: { currentPage: INITIAL_RECEIVE_PAGE_CURSOR, totalCount: 0, pageSize: INITIAL_RECEIVE_PAGE_SIZE },
};

/**
 * [입고관리 > 입고현황] 3PL 콘텐츠
 */
const ReceiveStatus3PLContent = ({ isFilterOpen }: { isFilterOpen: boolean }) => {
  const queryClient = useQueryClient();

  const receive3PLProgressExcelMutation = useGetInboundRequestProgressDownloadMutation();
  const receiveSignSpecificationMutation = useGetReceivingSignSpecificationMutation();
  const receive3PLProgressDetailMutation = useGetInboundRequestProgressByRequestIdMutation();

  const ownerCode = useUserInfoStore((state) => state.userInfo.ownerCode);

  const { data: filterOptions, isError: isFilterOptionsError } = useGetFilterAllQuery('INBOUND_REQUEST_PROGRESS', {
    silent: true,
  });

  const [searchFilterValues, setSearchFilterValues] = useState<TypeReceiveStatus3PLFilter>(
    DEFAULT_RECEIVE_STATUS_3PL_FILTER
  );
  const [searchFilterValuesStatus, setSearchFilterValuesStatus] = useState<TypeReceiveStatusFilterStatus>({
    dateRange: 'default',
  });

  const [listQueryParams, setListQueryParams] = useState<TypeGetInboundRequestProgressParams | null>(null);
  const listQuery = useGetInboundRequestProgressQuery({
    ownerCode,
    params: listQueryParams ?? ({} as TypeGetInboundRequestProgressParams),
    enabled: !!listQueryParams && !!ownerCode,
  });

  const [selectedItems, setSelectedItems] = useState<Type3PLReceiveStatusInfo[]>([]);

  const [receiveProgressDetailData, setReceiveProgressDetailData] = useState<Type3PLReceiveStatusDetail | null>(null);

  const { isDownloadReady, searchedParam, markDownloadReady, checkFilterChange } = useDownloadReady<
    TypeReceiveStatus3PLFilter,
    TypeGetInboundRequestProgressParams
  >({
    initialFilter: DEFAULT_RECEIVE_STATUS_3PL_FILTER,
  });

  const statusDetailModal = useModal();

  useEffect(() => {
    setSearchFilterValues((prev) => ({
      ...prev,
      keywordType: filterOptions?.keywordType[0] ?? null,
      dateType: filterOptions?.dateType[0] ?? null,
    }));
  }, [filterOptions]);

  useEffect(() => {
    if (isFilterOptionsError) {
      toastService.addToast({
        type: 'error',
        persist: true,
        message: renderFilterErrorMessage(),
      });
    }
  }, [isFilterOptionsError]);

  useEffect(() => {
    checkFilterChange(searchFilterValues);
  }, [searchFilterValues]);

  useEffect(() => {
    if (listQuery.data && listQueryParams && !listQuery.isPlaceholderData) {
      markDownloadReady({
        filterValue: searchFilterValues,
        hasData: listQuery.data.data.inboundRequestProgresses.length > 0,
        searchedParam: listQueryParams,
      });
    }
  }, [listQuery.data, listQueryParams, listQuery.isPlaceholderData]);

  const handleSearch = (filter: TypeReceiveStatus3PLFilter = searchFilterValues) => {
    if (!isValidDateRange(filter.dateRange, MAX_RECEIVE_DATE_RANGE)) {
      setSearchFilterValuesStatus((prev) => ({ ...prev, dateRange: 'error' }));
      toastService.addToast({
        type: 'error',
        message: `최대 ${MAX_RECEIVE_DATE_RANGE}일 기간으로 입력 후 검색해주세요.`,
      });
      return;
    }

    const params: TypeGetInboundRequestProgressParams = {
      ...addIf(filter.center?.value, 'cluster'),
      ...addIf(filter.temperature?.value, 'warehouse'),
      ...addIf(filter.keyword, 'codeType', filter.keywordType?.value ?? ''),
      ...addIf(filter.keyword, 'code'),
      dateType: filter.dateType?.value ?? '',
      dateFrom: formatDate(filter.dateRange?.start, 'YYYY-MM-DD') ?? '',
      dateTo: formatDate(filter.dateRange?.end, 'YYYY-MM-DD') ?? '',
      ...addIf(filter.status?.value, 'status'),
      ...addIf(filter.releaseCode?.value, 'releaseCode'),
      ...addIf(filter.disallowAllYesNo?.value, 'hasIssue'),
      hasReport: filter.hasReport?.value ?? null,
      reportStatus: filter.reportStatus?.value ?? null,
      limit: filter.pagination.pageSize,
      offset: filter.pagination.currentPage * filter.pagination.pageSize,
    };

    queryClient.invalidateQueries({
      queryKey: receiveKeys.inboundRequestProgress(ownerCode, params),
    });

    setSearchFilterValues(filter);
    setListQueryParams(params);
  };

  const handleReset = () => {
    setSearchFilterValues({
      ...DEFAULT_RECEIVE_STATUS_3PL_FILTER,
      keywordType: filterOptions?.keywordType[0] ?? null,
      dateType: filterOptions?.dateType[0] ?? null,
    });
    setSearchFilterValuesStatus((prev) => ({ ...prev, dateRange: 'default' }));
  };

  const handleClickExcelDownload = () => {
    if (!searchedParam) return;

    receive3PLProgressExcelMutation.mutate(
      { ownerCode, ...searchedParam },
      {
        onSuccess: () => toastService.addToast({ message: TOAST_MESSAGE.DOWNLOAD_SUCCESS }),
      }
    );
  };

  const handleChangeLimit = (pageSize: number) => {
    handleSearch({
      ...searchFilterValues,
      pagination: { ...searchFilterValues.pagination, pageSize, currentPage: INITIAL_RECEIVE_PAGE_CURSOR },
    });
  };

  const handleClickPage = (value: number) => {
    handleSearch({
      ...searchFilterValues,
      pagination: { ...searchFilterValues.pagination, currentPage: value },
    });
  };

  const handleChangeTableRowSelection = (selectedRows: IRowNode[]) => {
    setSelectedItems(selectedRows.map((row) => row.data));
  };

  const handleClickSpecificationDownload = () => {
    receiveSignSpecificationMutation.mutate(
      {
        ownerCode,
        requestNos: selectedItems.map((item) => item.requestCode).join(','),
      },
      {
        onSuccess: () => {
          toastService.addToast({ message: TOAST_MESSAGE.DOWNLOAD_SUCCESS });
        },
      }
    );
  };

  const handleViewStatusDetail = (selectedData: Type3PLReceiveStatusInfo) => {
    receive3PLProgressDetailMutation.mutate(
      { ownerCode, requestId: String(selectedData.requestId) },
      {
        onSuccess: ({ data }) => {
          setReceiveProgressDetailData(data);
          statusDetailModal.handleOpenModal();
        },
      }
    );
  };

  return (
    <>
      {isFilterOpen && (
        <ReceiveStatusSearchFilter
          options={filterOptions}
          values={searchFilterValues}
          valuesState={searchFilterValuesStatus}
          onChange={(values) => setSearchFilterValues((prev) => ({ ...prev, ...values }))}
          onChangeStatus={(values) => setSearchFilterValuesStatus((prev) => ({ ...prev, ...values }))}
          onSearch={() => handleSearch()}
          onReset={handleReset}
          reportFilters={{
            values: {
              hasReport: searchFilterValues.hasReport,
              reportStatus: searchFilterValues.reportStatus,
            },
            onChange: (values) => setSearchFilterValues((prev) => ({ ...prev, ...values })),
          }}
        />
      )}
      <ReceiveStatusTable
        mode="3PL"
        data={listQuery.data?.data.inboundRequestProgresses ?? null}
        pagination={{
          ...searchFilterValues.pagination,
          totalCount: listQuery.data?.pagination.listTotal ?? 0,
        }}
        isDownloadReady={isDownloadReady}
        selectedItems={selectedItems}
        onChangePageLimit={(opt) => handleChangeLimit(opt.value)}
        onChangePage={handleClickPage}
        onChangeRowSelection={handleChangeTableRowSelection}
        onClickViewDetail={handleViewStatusDetail}
        onClickReceiveStatusExcelDownload={handleClickExcelDownload}
        onClickSpecificationDownload={handleClickSpecificationDownload}
      />
      {statusDetailModal.isOpen && receiveProgressDetailData && (
        <ReceiveStatusDetailModal3PL
          open={statusDetailModal.isOpen}
          data={receiveProgressDetailData}
          onClose={statusDetailModal.handleCloseModal}
        />
      )}
    </>
  );
};

export default ReceiveStatus3PLContent;
